/**
 * `/evaluators/new` and `/evaluators/:id` — the configure screen. A new one
 * starts from `?kind=llm_judge|code` or `?template=<key>` (the template only
 * pre-fills; the evaluator does not remember it). An existing one is edited
 * from its latest version.
 */

import { useParams } from "react-router";
import { ButtonLink } from "@/components/ui/button";
import { EmptyState, ErrorState, Skeleton } from "@/components/ui/states";
import { useUrlState } from "@/lib/use-url-state";
import { ConfigureScreen } from "./configure-screen";
import { useCodeChecks, useEvaluatorTemplates, useEvaluators } from "./hooks";
import type { DraftSeed } from "./use-evaluator-draft";

const back = <ButtonLink to="/evaluators">Back to evaluators</ButtonLink>;

export function EvaluatorConfigurePage() {
  const { id } = useParams();
  const url = useUrlState();
  const templateKey = url.get("template");
  const evaluators = useEvaluators();
  const templates = useEvaluatorTemplates(!!templateKey);
  const checks = useCodeChecks();

  const saved = id ? evaluators.data?.find((e) => e.id === id) : undefined;
  const template = templateKey ? templates.data?.find((t) => t.key === templateKey) : undefined;
  const kind = saved?.kind ?? template?.kind ?? (url.get("kind") === "code" ? "code" : "llm_judge");

  const waiting = [
    id ? evaluators : null,
    templateKey ? templates : null,
    kind === "code" ? checks : null,
  ].filter((q) => q !== null);
  const failed = waiting.find((q) => q.isError);
  if (failed) return <ErrorState error={failed.error} what="the evaluator" />;
  if (waiting.some((q) => q.isPending)) return <Skeleton label="Loading the evaluator" />;
  if (id && !saved) return <EmptyState title="Evaluator not found" action={back} />;
  if (templateKey && !template) return <EmptyState title="Template not found" action={back} />;

  const seed: DraftSeed = saved
    ? {
        kind: saved.kind,
        name: saved.name,
        prompt: saved.prompt,
        check: saved.check,
        options: saved.options,
        mapping: saved.default_mapping,
        pass_threshold: saved.pass_threshold,
      }
    : template
      ? {
          kind: template.kind,
          name: template.name,
          prompt: template.prompt ?? null,
          check: template.check ?? null,
          options: template.options ?? null,
          mapping: template.mapping,
          category: template.category,
          description: template.description,
        }
      : { kind };

  return (
    <ConfigureScreen
      key={saved?.id ?? template?.key ?? kind}
      seed={seed}
      checks={checks.data ?? []}
      saved={saved}
    />
  );
}
